import { useCallback } from 'react';

import { AnalyticsEvent, useAnalytics } from '@bonusly/analytics';

type AmplitudeRecognitionType = 'peer_to_peer_recognition' | 'manual_award' | 'automated_award' | 'claimable_award';

interface AmplitudeEventProperties {
	amount?: number;
	celebrationType?: CelebrationType;
	display?: string;
	gifIncluded?: boolean;
	hashtagsCount?: number;
	hasLink?: boolean;
	imageIncluded?: boolean;
	isThankYou?: boolean;
	reason?: string;
	recipientsCount?: number;
	result?: 'success' | 'failure';
	url?: string;
	userTagsCount?: number;
	wordCount?: number;
	recognitionType?: AmplitudeRecognitionType;
	spotlightIncluded?: boolean;
	usedSuggestion?: boolean;
	suggestionModified?: boolean;
}

type CelebrationType = 'birthday' | 'welcome' | 'work_anniversary' | '';

export type ViewChangeEventProps = Required<Pick<AmplitudeEventProperties, 'display'>>;

export type CommentStartEventProps = Pick<AmplitudeEventProperties, 'celebrationType' | 'recognitionType' | 'isThankYou'>;

export type CommentSubmitFailureEventProps = Pick<AmplitudeEventProperties, 'reason'>;

export type CommentSubmitSuccessEventProps = Required<
	Pick<
		AmplitudeEventProperties,
		| 'amount'
		| 'gifIncluded'
		| 'hashtagsCount'
		| 'imageIncluded'
		| 'recipientsCount'
		| 'userTagsCount'
		| 'wordCount'
		| 'recognitionType'
		| 'isThankYou'
		| 'spotlightIncluded'
		| 'usedSuggestion'
		| 'suggestionModified'
	>
>;

export const useBonusAnalytics = () => {
	const { track, trackEvent } = useAnalytics();

	const trackCommentStart = useCallback(
		(eventProps: CommentStartEventProps) => {
			track(AnalyticsEvent.Recognition.CommentStart, {
				...eventProps,
			});
		},
		[track]
	);

	const trackCommentSubmitFailure = useCallback(
		(eventProps?: CommentSubmitFailureEventProps) => {
			track(AnalyticsEvent.Recognition.CommentSubmit, {
				...eventProps,
				result: 'failure',
			});
		},
		[track]
	);
	const trackCommentSubmitSuccess = useCallback(
		(eventProps: CommentSubmitSuccessEventProps) => {
			track(AnalyticsEvent.Recognition.CommentSubmit, {
				...eventProps,
				result: 'success',
			});
			trackEvent(AnalyticsEvent.Recognition.CommentAdded);
		},
		[track, trackEvent]
	);

	const trackViewChange = useCallback(
		(eventProps: ViewChangeEventProps) => {
			track(AnalyticsEvent.Recognition.ViewChange, {
				...eventProps,
			});
		},
		[track]
	);

	const trackSuggestedThankYouShown = useCallback(
		(eventProps: { suggestionsCount: number }) => {
			track(AnalyticsEvent.Recognition.SuggestedThankYouShown, eventProps);
		},
		[track]
	);

	const trackClickSuggestedThankYou = useCallback(
		(eventProps: { suggestionText: string; suggestionIndex: number; suggestionsCount: number }) => {
			track(AnalyticsEvent.Recognition.SuggestedThankYouClicked, eventProps);
		},
		[track]
	);

	const trackSuggestedThankYouDismissed = useCallback(() => {
		track(AnalyticsEvent.Recognition.SuggestedThankYouDismissed);
	}, [track]);

	return {
		trackCommentStart,
		trackCommentSubmitFailure,
		trackCommentSubmitSuccess,
		trackViewChange,
		trackSuggestedThankYouShown,
		trackClickSuggestedThankYou,
		trackSuggestedThankYouDismissed,
	};
};
